import { z } from "zod";
import { optionalUuid } from "../lib/zod-helpers";
import { paginationSchema, sortSchema } from "./pagination";

/**
 * HCA Moravia — validační schémata pro `hca.zpetnaVazba` tRPC router.
 *
 * Public procedury (`submit`, `getByToken`, `markGoogleReviewClicked`)
 * berou tokenString z URL QR kódu. Token je crypto.randomUUID (36 znaků)
 * — validace jen max() + min() bez specifického formátu.
 */

/** Token v URL — volný string, max 100 znaků (crypto.randomUUID = 36). */
export const publicTokenSchema = z
  .string()
  .min(8, "Token je příliš krátký")
  .max(100, "Token musí být max 100 znaků");

/** Public submit formuláře — veřejný formulář pod /zpetna-vazba/$token. */
export const submitFeedbackSchema = z.object({
  publicToken: publicTokenSchema,
  hodnoceni: z.coerce
    .number()
    .int()
    .min(1, "Hodnocení musí být 1 až 5")
    .max(5, "Hodnocení musí být 1 až 5"),
  komentar: z.string().max(2000, "Komentář musí být 2000 znaků nebo méně").optional(),
});

/** Načtení kontextu podle tokenu — public. */
export const getFeedbackByTokenSchema = z.object({
  publicToken: publicTokenSchema,
});

/** Tracking kliku na Google Review link. */
export const markGoogleReviewClickedSchema = z.object({
  publicToken: publicTokenSchema,
});

/** Admin — vygenerovat QR tokeny pro termin. */
export const generateQrTokensSchema = z.object({
  terminId: z.string().uuid("Neplatné ID termínu"),
});

/** Whitelist řazení zpětných vazeb — router mapuje klíče na sloupce. */
export const ZPETNA_VAZBA_SORT_KEYS = ["datum", "hodnoceni", "termin"] as const;
export type ZpetnaVazbaSortKey = (typeof ZPETNA_VAZBA_SORT_KEYS)[number];

/** Admin list feedbacků. */
export const listZpetnaVazbaSchema = paginationSchema
  .merge(sortSchema(ZPETNA_VAZBA_SORT_KEYS, "desc"))
  .extend({
    terminId: optionalUuid(),
    hodnoceniMin: z.coerce.number().int().min(1).max(5).optional(),
    submittedOnly: z.coerce.boolean().optional(),
  });

/** Admin — list per termín. */
export const listZpetnaVazbaByTerminSchema = z.object({
  terminId: z.string().uuid("Neplatné ID termínu"),
});

/** Admin — manuální flag, že účastník napsal Google recenzi. */
export const markGoogleReviewSubmittedSchema = z.object({
  id: z.string().uuid("Neplatné ID zpětné vazby"),
});

/** Admin ID schema. */
export const zpetnaVazbaIdSchema = z.object({
  id: z.string().uuid("Neplatné ID zpětné vazby"),
});

export type SubmitFeedbackInput = z.infer<typeof submitFeedbackSchema>;
export type GetFeedbackByTokenInput = z.infer<typeof getFeedbackByTokenSchema>;
export type MarkGoogleReviewClickedInput = z.infer<typeof markGoogleReviewClickedSchema>;
export type GenerateQrTokensInput = z.infer<typeof generateQrTokensSchema>;
export type ListZpetnaVazbaInput = z.infer<typeof listZpetnaVazbaSchema>;
export type ListZpetnaVazbaByTerminInput = z.infer<typeof listZpetnaVazbaByTerminSchema>;
export type MarkGoogleReviewSubmittedInput = z.infer<typeof markGoogleReviewSubmittedSchema>;
export type ZpetnaVazbaIdInput = z.infer<typeof zpetnaVazbaIdSchema>;
